import { type } from "arktype";
// Higher = more history ready. UNKNOWN ranks above HISTORICAL on purpose: an aggregator that
// doesn't report readiness must never be treated as "still preparing" (it would gate forever).
const TRANSACTIONS_READINESS_RANK = {
    NOT_READY: 0,
    INITIAL_UPDATE_COMPLETE: 1,
    HISTORICAL_UPDATE_COMPLETE: 2,
    UNKNOWN: 3,
};
/**
 * True iff `readiness` is at least `atLeast` in the prep progression. `undefined` (field absent)
 * is treated identically to `UNKNOWN` — i.e. "fully ready", so non-Plaid syncs are never gated.
 * Pure leaf; unit-tested in roo-shared `tests/transactionsReadiness.test.ts`.
 */
export function isTransactionsReadinessAtLeast(p) {
    var _a;
    const rank = TRANSACTIONS_READINESS_RANK[(_a = p.readiness) !== null && _a !== void 0 ? _a : "UNKNOWN"];
    return rank >= TRANSACTIONS_READINESS_RANK[p.atLeast];
}
// ------------------------
// Logs during syncing
// ------------------------
export var MembershipSyncEvent;
(function (MembershipSyncEvent) {
    MembershipSyncEvent["NEW_SYNC_FROM_INSTITUTION_TO_AGGREGATOR_DETECTED"] = "NEW_SYNC_FROM_INSTITUTION_TO_AGGREGATOR_DETECTED";
    MembershipSyncEvent["CONNECTION_STATUS_CODE_CHANGED"] = "CONNECTION_STATUS_CODE_CHANGED";
    MembershipSyncEvent["CONNECTION_STATUS_BROKEN"] = "CONNECTION_STATUS_BROKEN";
    MembershipSyncEvent["CONNECTION_STATUS_CONNECTED"] = "CONNECTION_STATUS_CONNECTED";
})(MembershipSyncEvent || (MembershipSyncEvent = {}));
export const membershipSyncEventSchema = type("'NEW_SYNC_FROM_INSTITUTION_TO_AGGREGATOR_DETECTED' | 'CONNECTION_STATUS_CODE_CHANGED' | 'CONNECTION_STATUS_BROKEN' | 'CONNECTION_STATUS_CONNECTED'").as();
export const membershipSyncChangeDetailsSchema = type({
    "[string]": "'insert' | 'update' | 'delete'",
});
export const membershipSyncDetailsSchema = type({
    status: "'pending' | 'success' | 'fail' | 'disconnected'",
    sourceId: "string",
    lastRefreshFromInstitutionToAggregatorIsoStr: "string",
    aggregatorConnected: "boolean",
    rowsTouched: "number",
    executionTimeMs: "number",
    events: membershipSyncEventSchema.array(),
    errorCount: "number",
    "errorMessages?": "string[]",
    changes: type({
        accountBySourceId: membershipSyncChangeDetailsSchema,
        transactionBySourceIds: membershipSyncChangeDetailsSchema,
    }),
    // How much transaction history the aggregator reported as ready on this sync. Set by Plaid's
    // syncTransactions from `transactions_update_status`; omitted by aggregators that don't report
    // it (treated as fully ready). Drives the connect-time completion gate in syncMembership.
    "transactionsReadiness?": "'UNKNOWN' | 'NOT_READY' | 'INITIAL_UPDATE_COMPLETE' | 'HISTORICAL_UPDATE_COMPLETE'",
    // Set only when `status === "fail"`: classifies the failure for the aggregator-errors alert gate.
    // "transient" iff EVERY accumulated error is a recovered-but-still-failed QuilttTransientError
    // (401-after-refresh / 429-after-backoff survivor) — suppressible, alerts only on >24h persistence.
    // "hard" = a genuine exception/bug — always alerts. Absent on non-fail outcomes. See
    // `shouldAlertOnSyncResult` in roo-server's aggregator.service.ts.
    "failureClass?": "'transient' | 'hard'",
});
// `MembershipEvent.event_meta_raw` is NOT a single shape — it is a forensic blob whose contents
// depend on the row's `event_type` column (a discriminator the JSONB validator can't see, since it
// selects only the column). `event_type: "SYNC"` writes `MembershipSyncDetails` (above); the
// aggregator-switching path (`switching.service.ts`, `event_type: "AGGREGATOR_SWITCH"`) writes the
// shape below. Any other `event_type` (ARCHIVE, NEW_ACCOUNTS_AVAILABLE, REQUEST_REFRESH…) leaves the
// column null. So the registered schema for the column must be the UNION of the non-null shapes.
//
// Only the switch's IDENTITY fields are required here — `switchPlanId` (also the de-facto
// discriminator: SYNC rows never carry it) plus the from/to aggregator sources. Every count/forensic
// field is optional ON PURPOSE: the AGGREGATOR_SWITCH payload has grown over time (`oldAccountsRemoved`
// arrived with T-87; the `duplicatesDropped`/`pendingsRescued`/`transactionSyncFloorIso` "v2 splice
// forensics" landed later), so historical rows — including a prod-forked staging DB's — legitimately
// lack the newer keys. Requiring them would just trade one false-positive for another. The source
// fields are `string` (not the `aggregator_source` enum) so a retired/renamed source value on an old
// row can't fail validation of a purely forensic record.
export const membershipSwitchDetailsSchema = type({
    switchPlanId: "string",
    fromAggregatorSource: "string",
    toAggregatorSource: "string",
    "accountsMatched?": "number",
    "accountsKeptNew?": "number",
    "oldAccountsRemoved?": "number",
    "transactionsMoved?": "number",
    "oldPendingsDropped?": "number",
    "duplicatesDropped?": "number",
    "pendingsRescued?": "number",
    "transactionSyncFloorIso?": "string | null",
});
/**
 * The full shape of `MembershipEvent.event_meta_raw` (nullable): the union of every non-null payload
 * the column can hold, keyed by the row's `event_type`. Registered in `JSONB_SCHEMA_REGISTRY` so
 * `validate-jsonb` accepts both SYNC and AGGREGATOR_SWITCH rows. A malformed SYNC row still fails
 * (it matches neither branch — no sync fields, and no `switchPlanId`).
 */
export const membershipEventMetaSchema = membershipSyncDetailsSchema.or(membershipSwitchDetailsSchema);
/**
 * Deep Link URL used by MX in the browser opened by the MX connection widget to notify the app
 * that a connection has been made.  For details, see handleMxDeepLink in agg.service.tsx.
 */
export const MX_REDIRECT_URL = {
    isRedirectUrl: (url) => {
        return url.includes("/mx/oauth");
    },
    //Passing information as a path parameter is the most reliable way to encode information we want back :shrug:
    getRedirectUrlWithData: (data) => {
        const base64 = btoa(JSON.stringify(data || {}))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, ""); // remove padding for compactness
        const url = `https://auth.roo.money/mx/oauth/${base64}/oauth_complete`;
        return url;
    },
    decodeRedirectUrlWithData: (url) => {
        var _a;
        const base64 = (_a = url.match(/\/mx\/oauth\/(.+?)\/oauth_complete/)) === null || _a === void 0 ? void 0 : _a[1];
        if (!base64) {
            return null;
        }
        const decodedJson = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(decodedJson);
    },
};
/**
 * Deep Link URL used by Quiltt to notify the connection widget of an new connection.  The
 * connection widget often handles this automatically, but may not (particularly for Android).
 * For details, see connectQuilttBank in agg.service.tsx in the mobile app.
 */
export const QUILTT_REDIRECT_URL = {
    isRedirectUrl: (url) => {
        return url.includes("/quiltt/oauth");
    },
    getRedirectUrl: () => "https://auth.roo.money/quiltt/oauth",
};
export const PLAID_REDIRECT_URL = {
    isRedirectUrl: (url) => {
        return url.includes("/plaid/oauth");
    },
    // This is the redirect uri registered in the Plaid dashboard for the Roo app
    getRedirectUrl: () => "https://auth.roo.money/plaid/oauth",
};
//# sourceMappingURL=aggregator.models.js.map